import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Tabs, Tab, Alert, Typography } from "@mui/material";
import { FolderSpecial as ProjectIcon } from "@mui/icons-material";
import BOMService from "../../../services/BOM.service";
import BOMDashboardList from "./BOMDashboardList";
import BOMPreviewDialog from "./BOMPreviewDialog";
import ProjectPicker from "../../pickers/ProjectPicker";

export default function BillOfMaterials() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState(0);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [archivedProject, setArchivedProject] = useState(null);
    const [archivedRows, setArchivedRows] = useState([]);
    const [archivedLoading, setArchivedLoading] = useState(false);

    const [previewDialog, setPreviewDialog] = useState({ open: false, project: null });
    const [deepLinkError, setDeepLinkError] = useState("");

    // Shop floor only cares about released BOMs — drafts aren't ready to
    // pull against yet, so keep them out of every list/lookup here.
    const onlyReleased = (data) => data.filter((r) => r.bom_status === "released");

    const refresh = useCallback(() => {
        setLoading(true);
        setError("");
        BOMService.getDashboard(false)
            .then((res) => setRows(onlyReleased(Array.isArray(res.data) ? res.data : [])))
            .catch(() => setError("Failed to load Bill of Materials data."))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (tab === 0) refresh();
    }, [tab, refresh]);

    // Deep link from a scanned BOM PDF QR code: /shop/bom?project=<id>&bom=<id>
    useEffect(() => {
        const projectId = searchParams.get("project");
        if (!projectId) return;
        const bomId = searchParams.get("bom");

        let active = true;
        setDeepLinkError("");

        const pickRow = (data) => {
            const released = onlyReleased(data);
            if (bomId) {
                const match = released.find((r) => String(r.bom_id) === String(bomId));
                if (match) return match;
            }
            return released[0] ?? null;
        };

        BOMService.getDashboard(false, projectId)
            .then((res) => {
                const data = Array.isArray(res.data) ? res.data : [];
                const row = pickRow(data);
                if (row) return row;
                // Not in the active list — check archived before giving up.
                return BOMService.getDashboard(true, projectId).then((r) => {
                    const archived = Array.isArray(r.data) ? r.data : [];
                    return pickRow(archived);
                });
            })
            .then((row) => {
                if (!active) return;
                if (row) {
                    setPreviewDialog({ open: true, project: row });
                } else {
                    setDeepLinkError("No BOM found for that project.");
                }
            })
            .catch(() => {
                if (active) setDeepLinkError("Failed to load that project's BOM.");
            });

        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const closePreview = () => {
        setPreviewDialog({ open: false, project: null });
        if (searchParams.get("project")) {
            setSearchParams({});
        }
    };

    const handleArchivedProjectChange = (project) => {
        setArchivedProject(project);
        if (!project) {
            setArchivedRows([]);
            return;
        }
        setArchivedLoading(true);
        setError("");
        BOMService.getDashboard(true, project.id)
            .then((res) => setArchivedRows(onlyReleased(Array.isArray(res.data) ? res.data : [])))
            .catch(() => setError("Failed to load archived BOM data."))
            .finally(() => setArchivedLoading(false));
    };

    return (
        <Box>
            <Tabs value={tab} onChange={(_, val) => setTab(val)} sx={{ mb: 2 }}>
                <Tab label="Active" />
                <Tab label="Archived" />
            </Tabs>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {deepLinkError && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    onClose={() => setDeepLinkError("")}
                >
                    {deepLinkError}
                </Alert>
            )}

            {tab === 0 && (
                <BOMDashboardList
                    rows={rows}
                    loading={loading}
                    onView={(row) => setPreviewDialog({ open: true, project: row })}
                />
            )}

            {tab === 1 && (
                <Box>
                    <Box sx={{ mb: 2 }}>
                        <ProjectPicker
                            value={archivedProject}
                            onChange={handleArchivedProjectChange}
                            label="Search archived project"
                            size="small"
                        />
                    </Box>
                    {!archivedProject ? (
                        <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                            <ProjectIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                            <Typography variant="body2">
                                Search for a project above to view its archived BOM.
                            </Typography>
                        </Box>
                    ) : (
                        <BOMDashboardList
                            rows={archivedRows}
                            loading={archivedLoading}
                            onView={(row) => setPreviewDialog({ open: true, project: row })}
                        />
                    )}
                </Box>
            )}

            <BOMPreviewDialog
                open={previewDialog.open}
                onClose={closePreview}
                project={previewDialog.project}
            />
        </Box>
    );
}
